import React__default, { useRef, useLayoutEffect } from 'react';
import { OverlayTriggerStateContext, ModalOverlay, Modal, Dialog, DialogTrigger } from 'react-aria-components';
import { CloseButton } from '../atoms/Button.js';
import { toSpacingAttributes } from '../layouts/Spacing/spacing.js';
import { isButtonComponent } from '../utils/isButtonComponent.js';
import { TitleGroup } from './TitleGroup.js';
import './Popup.scss';

/**
 * A simple popup component that wraps react-aria-components Dialog and Modal.
 *
 * ```tsx
 * <PopupTrigger>
 *   <Button>Show popup</Button>
 *   <Popup title="My Popup">
 *     <div>Popup content</div>
 *   </Popup>
 * </PopupTrigger>
 * ```
 *
 * Or with a render function to access the close function:
 *
 * ```tsx
 * <PopupTrigger>
 *   <Button>Show popup</Button>
 *   <Popup title="My Popup">
 *     {(close) => (
 *       <div>
 *         <div>Popup content</div>
 *         <Button onPress={close}>Close</Button>
 *       </div>
 *     )}
 *   </Popup>
 * </PopupTrigger>
 * ```
 */
const Popup = ({ children, hasCloseButton = true, title, description, className, rounding = '10', contentPadding = { b: '20' }, isDismissable = true, isKeyboardDismissDisabled = false, initialFocus = 'input', size = 'small', }) => {
    const dialogRef = useRef(null);
    const popupState = React__default.useContext(OverlayTriggerStateContext);
    // Generate unique IDs for aria-labelledby and aria-describedby
    const titleId = React__default.useId();
    const descriptionId = React__default.useId();
    // Focus the initial element when the popup opens
    useLayoutEffect(() => {
        if (initialFocus && dialogRef.current && popupState.isOpen) {
            const elementToFocus = dialogRef.current.querySelector(initialFocus);
            elementToFocus?.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: ref.current needed to detect DOM readiness for focus
    }, [initialFocus, dialogRef.current, popupState.isOpen]);
    // Memoize the close callback to prevent unnecessary re-renders of children
    const closePopup = React__default.useCallback(() => popupState.close(), [popupState]);
    // Compute content once - avoids recreating a component function on every render
    const popupContent = typeof children === 'function' ? children(closePopup) : children;
    return (React__default.createElement(ModalOverlay, { "data-rev-component": 'popup', className: className || '', "data-rev-size": size, isDismissable: isDismissable, isKeyboardDismissDisabled: isKeyboardDismissDisabled, shouldCloseOnInteractOutside: (element) => {
            // Don't dismiss when interacting with React Aria popovers
            // (dropdowns, menus, listboxes) that portal outside the dialog
            // but logically belong to it — e.g. address autocomplete or state selector.
            return !element.closest('[role="menu"], [role="listbox"], [role="presentation"]');
        } },
        React__default.createElement(Modal, { className: 'popup-modal--rev' },
            React__default.createElement("div", { className: 'popup-modal-overlay--rev' }),
            React__default.createElement(Dialog, { ref: dialogRef, className: 'popup-dialog--rev', "data-rev-has-close-button": hasCloseButton, "data-rev-has-title": !!title, "data-rev-has-content": !!children, "data-rev-rounding": rounding, "aria-labelledby": title ? titleId : undefined, "aria-describedby": description ? descriptionId : undefined },
                React__default.createElement("header", { className: 'popup-header--rev' },
                    (title || description) && (React__default.createElement(TitleGroup, { className: 'popup-title-group--rev', title: title, description: description, level: 'subsection', titleId: titleId, descriptionId: descriptionId })),
                    hasCloseButton && (React__default.createElement(CloseButton, { className: 'popup-close-button--rev', variant: 'secondary', "aria-label": 'Close dialog', size: 'medium', onPress: () => popupState.close() }))),
                children && (React__default.createElement("div", { className: 'popup-content--rev', ...toSpacingAttributes('p', contentPadding) }, popupContent))))));
};
/**
 * A trigger component that wraps a Button and a Popup.
 *
 * Supports two modes:
 * 1. **Uncontrolled (with trigger)**: Pass a Button as the first child to trigger the popup
 * 2. **Controlled (no trigger)**: Use `isOpen` and `onOpenChange` props to control the popup externally
 *
 * ```tsx
 * // Uncontrolled mode with trigger button
 * <PopupTrigger>
 *   <Button>Show popup</Button>
 *   <Popup title="My Popup">
 *     <div>Popup content</div>
 *   </Popup>
 * </PopupTrigger>
 *
 * // Controlled mode without trigger
 * <PopupTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
 *   <Popup title="My Popup">
 *     <div>Popup content</div>
 *   </Popup>
 * </PopupTrigger>
 * ```
 */
const PopupTrigger = ({ children, isOpen, onOpenChange }) => {
    // Check if the first child is a Button component and
    // enhance it with aria-haspopup if it is
    const enhancedChildren = React__default.Children.map(children, (child, index) => {
        // Only process the first child (the trigger element)
        if (index === 0 && React__default.isValidElement(child)) {
            if (isButtonComponent(child)) {
                // Magically add the aria-haspopup attribute
                return React__default.cloneElement(child, {
                    ...child.props,
                    ariaHaspopup: 'dialog',
                });
            }
        }
        return child;
    });
    return (React__default.createElement(DialogTrigger, { isOpen: isOpen, onOpenChange: onOpenChange }, enhancedChildren));
};

export { Popup, PopupTrigger };
//# sourceMappingURL=Popup.js.map
